// src/pages/public/OrgSite.js — 테넌트(고객사) 사이트 (/o/:orgSlug)
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo, { breadcrumbLd, SITE_URL, DEFAULT_OG_IMAGE } from '../../components/common/Seo';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import { organizationService } from '../../services/organizationService';
import { eventService } from '../../services/eventService';
import { formatDate } from '../../utils/format';
import Loading from '../../components/common/Loading';
import PublicHeader from '../../components/common/PublicHeader';
import PublicFooter from '../../components/common/PublicFooter';
import './OrgSite.scss';

const OrgSite = () => {
  const { orgSlug } = useParams();
  const [org, setOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgData, eventList] = await Promise.all([
          organizationService.getBySlugPublic(orgSlug),
          eventService.getEventsByOrgSlug(orgSlug),
        ]);
        if (cancelled) return;
        setOrg(orgData);
        setEvents(eventList || []);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError('주최사 페이지를 찾을 수 없습니다');
        } else {
          setError('정보를 불러오지 못했습니다');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgSlug]);

  if (loading) return <div className="org-site"><PublicHeader /><Loading /></div>;

  if (error) {
    return (
      <div className="org-site">
        <Seo title="주최사 페이지를 찾을 수 없습니다 | 게이트온" description={error} noindex />
        <PublicHeader />
        <main id="main" className="org-site__error">
          <h1>{error}</h1>
          <Link to="/" className="gt-btn gt-btn--primary">홈으로</Link>
        </main>
        <PublicFooter />
      </div>
    );
  }

  // 진행 중인 행사가 없는 고객사 페이지는 내용이 빈약 → 검색 색인 제외
  const _plain = (org.description || '').replace(/\s+/g, ' ').trim();
  const _desc = _plain
    ? (_plain.length > 140 ? `${_plain.slice(0, 140).replace(/\s+\S*$/, '')}…` : _plain)
    : `${org.name}에서 진행하는 행사를 모았습니다. 게이트온에서 일정과 장소를 확인하고 티켓을 예매하세요.`;
  const _image = org.logo_url
    ? (org.logo_url.startsWith('http') ? org.logo_url : `${SITE_URL}${org.logo_url}`)
    : DEFAULT_OG_IMAGE;

  return (
    <div className="org-site">
      <Seo
        title={`${org.name} 행사 예매 | 게이트온`}
        description={_desc}
        path={`/o/${org.slug}`}
        image={_image}
        noindex={events.length === 0}
        jsonLd={breadcrumbLd([{ name: '홈', path: '/' }, { name: org.name, path: `/o/${org.slug}` }])}
      />
      <PublicHeader />

      {/* 조직 헤더 */}
      <header className="org-site__header">
        <div className="org-site__header-inner">
          {org.logo_url ? (
            <img className="org-site__logo" src={org.logo_url} alt={`${org.name} 로고`} />
          ) : (
            <div className="org-site__logo-placeholder">
              {org.name?.charAt(0) || 'G'}
            </div>
          )}
          <div className="org-site__brand">
            <span className="org-site__eyebrow">주최</span>
            <h1>{org.name}</h1>
            {org.description && <p className="org-site__desc">{org.description}</p>}
            {events.length > 0 && (
              <p className="org-site__count">지금 예매 중인 행사가 <strong>{events.length}개</strong> 있습니다.</p>
            )}
          </div>
        </div>
      </header>

      {/* 이벤트 그리드 */}
      <main id="main" className="org-site__main">
        <div className="org-site__main-inner">
          <div className="org-site__section-head">
            <h2>예매 중인 행사</h2>
            <p>카드를 누르면 해당 행사의 예매 페이지로 이동합니다.</p>
          </div>

          {events.length === 0 ? (
            <div className="org-site__empty">
              <Calendar size={48} strokeWidth={1.4} />
              <h3>지금 예매 중인 행사가 없습니다</h3>
              <p>새 행사가 열리면 이곳에 표시됩니다.</p>
            </div>
          ) : (
            <div className="org-site__grid">
              {events.map((event) => (
                <Link
                  key={event.id}
                  to={`/e/${event.slug}`}
                  className="org-event-card"
                >
                  <div className="org-event-card__image">
                    {event.thumbnail_url ? (
                      <img src={event.thumbnail_url} alt={event.name} loading="lazy" decoding="async" />
                    ) : (
                      <div className="org-event-card__placeholder" />
                    )}
                  </div>
                  <div className="org-event-card__body">
                    <h3>{event.name}</h3>
                    <div className="org-event-card__meta">
                      <span><Calendar size={14} strokeWidth={2} /> {formatDate(event.start_date)} ~ {formatDate(event.end_date)}</span>
                      {event.location && <span><MapPin size={14} strokeWidth={2} /> {event.location}</span>}
                    </div>
                    <span className="org-event-card__cta">
                      예매 페이지 보기 <ArrowRight size={14} strokeWidth={2.4} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="org-site__footer">
        <div className="org-site__footer-inner">
          <p>powered by <Link to="/">GATEON</Link></p>
          <div className="org-site__footer-links">
            <Link to="/terms">이용약관</Link>
            <Link to="/privacy">개인정보처리방침</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OrgSite;
